import type { AssistantMetricChartDTO } from '../../shared/assistant.js';
/**
 * Every in-app URL is built here, so a route change is one edit. `/` is the workspace overview;
 * products are the top level: `/p/:slug` (its overview) and its tabs; `/tasks` is the board
 * across all products.
 */
const enc = encodeURIComponent;

export const assistantPath = (product?: string): string =>
  product ? `/assistant?product=${enc(product)}` : '/assistant';

/** Every product's verdict and what waits for the viewer. */
export const homePath = '/';
export const startPath = '/start';

export const PRODUCT_TABS = [
  'overview',
  'tasks',
  'strategy',
  'workflow',
  'metrics',
  'money',
  'seo',
  'quality',
  'setup',
] as const;
export type ProductTab = (typeof PRODUCT_TABS)[number];

/** `/p/nopa` (the overview: North Star, goals, what needs attention), `/p/nopa/tasks`, … */
export const productPath = (slug: string, tab: ProductTab = 'overview'): string =>
  tab === 'overview' ? `/p/${enc(slug)}` : `/p/${enc(slug)}/${tab}`;

/** The overview with one goal's sheet open. */
export const goalPath = (slug: string, goalId: string): string =>
  `/p/${enc(slug)}?goal=${enc(goalId)}`;

/** The metrics explorer: old two-argument callers keep defaults; a resolved chart pins its exact view. */
export const metricPath = (
  slug: string,
  ref: string,
  chart?: Pick<AssistantMetricChartDTO, 'from' | 'to' | 'granularity' | 'compare'>,
): string => {
  const base = `${productPath(slug, 'metrics')}?ref=${enc(ref)}`;
  if (!chart) return base;
  const query = new URLSearchParams({
    range: 'custom',
    from: chart.from,
    to: chart.to,
    g: chart.granularity,
    compare: chart.compare ? '1' : '0',
  });
  return `${base}&${query}`;
};

/**
 * A/B tests live inside «Метрики» (navigation map HUB-81), not as a tab of their own;
 * `/p/:slug/experiments` redirects here.
 */
export const experimentsPath = (slug: string): string =>
  `${productPath(slug, 'metrics')}/experiments`;

/** The cross-product board, or one task's drawer over it. */
/** Workspace observations; these are not a product money ledger. */
export const agentCostsPath = '/agent-costs';

/** One product data entry, retaining all existing metric URLs and parameters. */
export const productDataPath = (slug: string): string => productPath(slug, 'metrics');

export const tasksPath = '/tasks';
export const taskPath = (key: string): string => `/tasks/${enc(key)}`;

/** A task's drawer over its product's board. */
export const productTaskPath = (slug: string, key: string): string =>
  `/p/${enc(slug)}/tasks/${enc(key)}`;

/** A site's SEO report, inside the product it belongs to (`/p/:slug/sites/…` redirects here). */
export const sitePath = (slug: string, domain: string): string =>
  `/p/${enc(slug)}/seo/${enc(domain)}`;

/** The board a path belongs to: the product's when inside one, the cross-product one otherwise. */
export function boardOf(pathname: string): string {
  const m = /^\/p\/([^/]+)/.exec(pathname);
  return m ? `/p/${m[1]}/tasks` : tasksPath;
}

/**
 * Search params that only the board reads. `/p/:slug` was the board before the overview took
 * its place; a link carrying any of these still means the board.
 */
export const BOARD_PARAMS = [
  'new',
  'q',
  'assignee',
  'label',
  'agents',
  'needs',
  'view',
  'lens',
  'cycle',
];

/** The product slug in a `/p/:slug/...` path, if any. */
export function productIn(pathname: string): string | null {
  const m = /^\/p\/([^/]+)/.exec(pathname);
  return m ? decodeURIComponent(m[1]!) : null;
}

/** Which product tab a path shows; null outside a product. */
export function tabOf(pathname: string): ProductTab | null {
  const m = /^\/p\/[^/]+(?:\/([^/]+))?/.exec(pathname);
  if (!m) return null;
  const seg = m[1];
  if (!seg) return 'overview';
  return (PRODUCT_TABS as readonly string[]).includes(seg) ? (seg as ProductTab) : null;
}

/** Where the team's name leads (HUB-101): the owner to «Настройки команды», anyone else home. */
export const teamHref = (role: string): string => (role === 'owner' ? '/team#settings' : '/');
